'use client';!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:offsoo-landing":true});}catch(e){}}();

import { useEffect, useState } from 'react'

import { RegisterCompanyStep } from './register-company-step'
import { RegisterCreatingStep } from './register-creating-step'
import { RegisterEmailStep } from './register-email-step'
import { RegisterPasswordStep } from './register-password-step'
import { RegisterStepProgress } from './register-step-progress'
import type { RegisterCopy, RegisterData, RegisterUi } from './register-types'
import { RegisterUserStep } from './register-user-step'

export type RegisterFormProps = {
  /** Page locale, threaded down to the reCAPTCHA widget so it speaks the visitor's language. */
  locale: string
  copy: RegisterCopy
  ui: RegisterUi
  /** Step captions for the segmented progress indicator, in step order (email, user, company, password). */
  stepLabels: string[]
  /** Template with {current}/{total}/{label} placeholders, e.g. "Step {current} of {total} · {label}". */
  stepTemplate: string
  /** Persistent page header (heading + "already have an account?" link), shown above every input step. */
  header?: React.ReactNode
}

export function RegisterForm({ locale, copy, ui, stepLabels, stepTemplate, header }: RegisterFormProps) {
  const [formData, setFormData] = useState<RegisterData>({})
  const [activeStep, setActiveStep] = useState(0)

  // Campaign and verification links deep-link into the wizard with `?email=…`. That has to happen
  // after mount: the server HTML always shows step 0, so seeding state from window.location during
  // the first client render would render step 1 and make hydration mismatch on exactly those links.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const email = params.get('email') || undefined
    const userId = params.get('userId') || undefined
    const userExternalId = params.get('userExternalId') || undefined
    if (!email && !userId && !userExternalId) {
      return
    }
    // eslint-disable-next-line react-hooks/set-state-in-effect -- deep-link init cannot happen during render: the server HTML always shows step 0, so advancing before hydration completes would mismatch
    setFormData((current) => ({ email, userId, userExternalId, ...current }))
    setActiveStep((step) => (step === 0 ? 1 : step))
  }, [])

  const updateData = (data: Partial<RegisterData>) => {
    setFormData((current) => ({ ...current, ...data }))
  }

  const nextStep = (data: Partial<RegisterData>) => {
    updateData(data)
    setActiveStep((step) => step + 1)
  }

  const previousStep = (data: Partial<RegisterData>) => {
    updateData(data)
    setActiveStep((step) => Math.max(0, step - 1))
  }

  const restartFlow = (data: RegisterData) => {
    updateData(data)
    setActiveStep(0)
  }

  return (
    <div className="flex flex-col gap-6">
      {activeStep < stepLabels.length && (
        <div className="flex flex-col gap-6">
          <RegisterStepProgress
            current={activeStep}
            total={stepLabels.length}
            label={stepLabels[activeStep]}
            template={stepTemplate}
          />
          {header}
        </div>
      )}

      {activeStep === 0 && (
        <RegisterEmailStep locale={locale} copy={copy.email} ui={ui} defaultValues={formData} onNext={nextStep} />
      )}
      {activeStep === 1 && (
        <RegisterUserStep ui={ui} defaultValues={formData} onNext={nextStep} onPrevious={previousStep} />
      )}
      {activeStep === 2 && (
        <RegisterCompanyStep
          copy={copy.company}
          ui={ui}
          defaultValues={formData}
          onNext={nextStep}
          onPrevious={previousStep}
        />
      )}
      {activeStep === 3 && (
        <RegisterPasswordStep
          copy={copy.password}
          ui={ui}
          defaultValues={formData}
          onNext={nextStep}
          onPrevious={previousStep}
        />
      )}
      {activeStep === 4 && <RegisterCreatingStep copy={copy.creating} data={formData} onRestart={restartFlow} />}
    </div>
  )
}
